import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FilterIcon from "../../assets/image/filter.png";
import FilterIconSvg from "../../assets/image/filter.svg";
import { formatRangeLabel, parseApiDate, toApiDate } from "../../utils/dateRange";
import DateRangeAppliedLabel from "./DateRangeAppliedLabel";

const CalendarPanel = ({ startDate, endDate, onChange, maxDate = new Date() }) => (
  <DatePicker
    selected={startDate}
    onChange={onChange}
    startDate={startDate}
    endDate={endDate}
    selectsRange
    inline
    showMonthDropdown
    showYearDropdown
    dropdownMode="select"
    maxDate={maxDate}
  />
);

/**
 * Same range calendar as fuel settlement details (filter icon + inline calendar).
 */
const DateRangeFilter = ({
  variant = "embedded",
  from = "",
  to = "",
  appliedFrom = "",
  appliedTo = "",
  onChange,
  range,
  onRangeChange,
  onApply,
  onClear,
  applyDisabled = false,
  error = "",
  label = "Date range",
  maxDate = new Date(),
  className = "",
  showAppliedLabel = false,
  appliedLabelPrefix = "Date range: ",
  toolbarIcon = false,
  bare = false,
}) => {
  const ref = useRef(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  useEffect(() => {
    if (variant === "embedded") return undefined;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [variant]);

  if (variant === "popover") {
    const [startDate, endDate] = range ?? [null, null];
    const picker = (
      <div className="relative shrink-0" ref={ref}>
        <button
          type="button"
          onClick={() => setIsPickerOpen(!isPickerOpen)}
          className={
            toolbarIcon
              ? "inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white p-0 outline-none hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-0"
              : "border-0 bg-transparent p-2 outline-none"
          }
          aria-label="Date range"
        >
          <img
            src={toolbarIcon ? FilterIconSvg : FilterIcon}
            alt=""
            className={
              toolbarIcon
                ? "h-5 w-5 invert grayscale brightness-100"
                : "h-9 w-9"
            }
          />
        </button>
        {isPickerOpen && (
          <div className="absolute left-0 z-50 mt-2 rounded-lg bg-white p-3 shadow-lg">
            <CalendarPanel
              startDate={startDate}
              endDate={endDate}
              onChange={onRangeChange}
              maxDate={maxDate}
            />
            <div className="mt-2.5 flex gap-2 border-t border-gray-100">
              <button
                type="button"
                className="btn btn-primary flex-1 !py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                disabled={applyDisabled}
                onClick={() => {
                  const ok = onApply?.();
                  if (ok !== false) setIsPickerOpen(false);
                }}
              >
                Apply
              </button>
              <button
                type="button"
                className="btn flex-1 !py-2 text-sm border border-gray-300 bg-white text-black hover:bg-gray-50"
                onClick={() => {
                  onClear?.();
                  setIsPickerOpen(false);
                }}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    );

    if (bare) {
      return picker;
    }

    return (
      <div className={`inline-flex items-center gap-x-2.5 ${className}`}>
        {picker}
        {showAppliedLabel && (
          <DateRangeAppliedLabel
            from={appliedFrom}
            to={appliedTo}
            prefix={appliedLabelPrefix}
          />
        )}
      </div>
    );
  }

  const startDate = parseApiDate(from);
  const endDate = parseApiDate(to);

  const handleStringRangeChange = (update) => {
    const [start, end] = update;
    onChange?.({
      from: toApiDate(start),
      to: toApiDate(end),
    });
    if (variant === "field" && start && end && !onApply) {
      setIsPickerOpen(false);
    }
  };

  if (variant === "embedded") {
    return (
      <div className={className}>
        <CalendarPanel
          startDate={startDate}
          endDate={endDate}
          onChange={handleStringRangeChange}
          maxDate={maxDate}
        />
        {error ? <p className="mb-0 mt-2 text-xs text-red-500">{error}</p> : null}
      </div>
    );
  }

  const display = formatRangeLabel(from, to, { emptyLabel: "Select date range" });

  const appliedDisplayFrom = appliedFrom || from;
  const appliedDisplayTo = appliedTo || to;

  return (
    <div className={className}>
      {label ? (
        <label className="mb-1 block text-sm font-UbuntuMedium text-gray-700">{label}</label>
      ) : null}
      <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsPickerOpen(!isPickerOpen)}
        className={`flex h-[44px] min-w-[14rem] items-center justify-between rounded-lg border bg-white px-3 text-sm outline-none focus:border-[#0B3E64] ${
          error ? "border-red-400" : "border-[#0B3E64]/40"
        }`}
      >
        <span className={from && to ? "text-gray-800" : "text-gray-500"}>{display}</span>
        <img src={FilterIconSvg} alt="" className="ml-2 h-7 w-7 shrink-0 invert grayscale brightness-100" />
      </button>
      {isPickerOpen && (
        <div className="absolute z-50 mt-2 rounded-lg bg-white p-3 shadow-lg">
          <CalendarPanel
            startDate={startDate}
            endDate={endDate}
            onChange={handleStringRangeChange}
            maxDate={maxDate}
          />
          {onApply ? (
            <div className="mt-2.5 flex gap-2 border-t border-gray-100">
              <button
                type="button"
                className="btn btn-primary flex-1 !py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                disabled={applyDisabled}
                onClick={() => {
                  const ok = onApply?.();
                  if (ok !== false) setIsPickerOpen(false);
                }}
              >
                Apply
              </button>
              <button
                type="button"
                className="btn flex-1 !py-2 text-sm border border-gray-300 bg-white text-black hover:bg-gray-50"
                onClick={() => {
                  if (onClear) {
                    onClear();
                  } else {
                    onChange?.({ from: "", to: "" });
                  }
                  setIsPickerOpen(false);
                }}
              >
                Clear
              </button>
            </div>
          ) : (from || to) && (
            <div className="mt-3 border-t border-gray-100 pt-3">
              <button
                type="button"
                className="btn w-full !py-2 text-sm border border-gray-300 bg-white text-black hover:bg-gray-50"
                onClick={() => {
                  onChange?.({ from: "", to: "" });
                  setIsPickerOpen(false);
                }}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
      {error ? <p className="mb-0 mt-1 text-xs text-red-500">{error}</p> : null}
      </div>
      {showAppliedLabel && (
        <p className="mb-0 mt-2 text-sm text-gray-600">
          <span className="font-UbuntuMedium text-gray-800">
            {formatRangeLabel(appliedDisplayFrom, appliedDisplayTo, { emptyLabel: "All dates" })}
          </span>
        </p>
      )}
    </div>
  );
};

export default DateRangeFilter;
